import {isEmpty, isEqual} from 'lodash/fp';
import {pick} from 'lodash';
import {toast} from 'sonner';

import {
  InclusionDateType,
  Period,
  PeriodType,
  PolicyAmountType,
  PolicyFilter,
  PolicyStatusType,
} from 'modules/decisionsNext/api/types';
import {Falsy} from 'types';

import {dayjs, formatDateToString} from 'utils/dateUtils';
import {deepDiff} from 'utils/deepDiff';
import {IS_DEV_ENV} from 'config';
import {EnnablLogger} from 'utils/ennabl';
import {checkFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';

import {initialFilter} from 'modules/decisionsNext/components/BaseFilter/utils/filter';
import {hasInvalidDateRange} from 'modules/decisionsNext/components/BaseFilter/utils/period';

const formatDateOrNullWithFallback = (value: string | number | null | undefined, fallback: string) => {
  return value !== undefined ? (value === null ? value : formatDateToString(value)) : fallback;
};

type GlobalFilter = Pick<
  PolicyFilter,
  | 'additionalPeriods'
  | 'brokerMarketStatuses'
  | 'childIntermediaryIds'
  | 'clientAgencyCodes'
  | 'companyFilters'
  | 'datasourceType'
  | 'departmentIds'
  | 'departmentLevel'
  | 'direct'
  | 'industryGroupIds'
  | 'industryIds'
  | 'industrySubGroupIds'
  | 'intermediaryIds'
  | 'issuingPaperIds'
  | 'location'
  | 'naicsSourceConfidenceScore'
  | 'metadata'
  | 'parentCarrierIds'
  | 'parentIntermediaryIds'
  | 'period'
  | 'policyAgencyCodes'
  | 'policySources'
  | 'policySourcesFilter'
  | 'policyStatusType'
  | 'policyStatuses'
  | 'policyStatusesAnd'
  | 'productGroupIds'
  | 'productIds'
  | 'productInsuranceSegmentIds'
  | 'productSuperGroupIds'
  | 'retailAgencyCodes'
  | 'ultimateCarrierIds'
  | 'ultimateIntermediaryIds'
  | 'userFilters'
>;

// Used object instead of array to ensure that all keys are contained from the GlobalFilter type
export const globalFilterKeysMap: Record<keyof GlobalFilter, boolean> = {
  additionalPeriods: true,
  brokerMarketStatuses: true,
  childIntermediaryIds: true,
  clientAgencyCodes: true,
  companyFilters: true,
  datasourceType: true,
  departmentIds: true,
  departmentLevel: true,
  direct: true,
  industryGroupIds: true,
  industryIds: true,
  industrySubGroupIds: true,
  intermediaryIds: true,
  issuingPaperIds: true,
  location: true,
  naicsSourceConfidenceScore: true,
  metadata: true,
  parentCarrierIds: true,
  parentIntermediaryIds: true,
  period: true,
  policyAgencyCodes: true,
  policySources: true,
  policySourcesFilter: true,
  policyStatusesAnd: true,
  policyStatusType: true,
  policyStatuses: true,
  productGroupIds: true,
  productIds: true,
  productInsuranceSegmentIds: true,
  productSuperGroupIds: true,
  retailAgencyCodes: true,
  ultimateCarrierIds: true,
  ultimateIntermediaryIds: true,
  userFilters: true,
};

// Sync with features/GlobalFilterProvider/hooks/useFormatGlobalFilter.ts
export const formatPolicyFilter = (filter: PolicyFilter, customFilterKeys: string[] = []): GlobalFilter => {
  const formattedFilter: GlobalFilter = pick(filter, Object.keys(globalFilterKeysMap).concat(customFilterKeys));
  const formattedPeriodByType = formatPolicyFilterPeriod(formattedFilter.period);

  if (!isEqual(formattedFilter, filter)) {
    const diff = deepDiff(formattedFilter, filter);

    if (IS_DEV_ENV) {
      EnnablLogger.error('Omitted filter data', {formattedFilter, globalFilter: filter, diff});
      toast.error(`Omitted filter data: ${JSON.stringify(diff)}`);
      throw new Error(`Omitted filter data: ${JSON.stringify(diff)}`);
    }

    EnnablLogger.error('Omitted filter data', diff);
  }

  const isMGADashboard = checkFeatureFlag(FeatureFlags.MGADashboard);
  const withPolicyStatusFilter = checkFeatureFlag(FeatureFlags.PolicyStatusFilter);
  const withClientTagsFilter = checkFeatureFlag(FeatureFlags.ClientTagsFilter) && !isMGADashboard;
  const withPolicyTagsFilter = checkFeatureFlag(FeatureFlags.PolicyTagsFilter) && !isMGADashboard;
  const withPolicySourceCodes = checkFeatureFlag(FeatureFlags.PolicySourceCodes);
  const withAmsProducerCodes = checkFeatureFlag(FeatureFlags.AmsProducerCodes);
  const withAccountExecutives = checkFeatureFlag(FeatureFlags.AccountExecutives);
  const withAnnualizedAmountType = checkFeatureFlag(FeatureFlags.AnnualizedValues);

  const isAnnualizedProhibited =
    (isMGADashboard || !withAnnualizedAmountType) &&
    formattedPeriodByType.amountType === PolicyAmountType.AnnualizedWritten;

  const formattedPeriod = isAnnualizedProhibited
    ? {...formattedPeriodByType, amountType: PolicyAmountType.Written}
    : formattedPeriodByType;

  const formattedStatusesAnd =
    !formattedFilter.policyStatusesAnd && formattedFilter.policyStatuses
      ? [{statuses: formattedFilter.policyStatuses}]
      : formattedFilter.policyStatusesAnd;

  return {
    ...formattedFilter,
    policyStatusType: withPolicyStatusFilter ? formattedFilter.policyStatusType : undefined,
    policyStatuses: undefined,
    policyStatusesAnd:
      !withPolicyStatusFilter && formattedFilter.policyStatusType === PolicyStatusType.Ams
        ? undefined
        : formattedStatusesAnd,
    clientAgencyCodes: withClientTagsFilter ? formattedFilter.clientAgencyCodes : undefined,
    retailAgencyCodes: isMGADashboard ? formattedFilter.retailAgencyCodes : undefined,
    policySourcesFilter:
      withPolicyTagsFilter && withPolicySourceCodes ? formattedFilter.policySourcesFilter : undefined,
    policyAgencyCodes: withPolicyTagsFilter ? formattedFilter.policyAgencyCodes : undefined,
    userFilters: {
      ...formattedFilter.userFilters,
      accountExecutiveIdsAndSourceCodes: withAccountExecutives
        ? formattedFilter.userFilters?.accountExecutiveIdsAndSourceCodes?.map(({userId, sourceCodes}) => {
            return {
              userId,
              sourceCodes: withAmsProducerCodes ? sourceCodes : undefined,
            };
          })
        : undefined,
      producerIdsAndSourceCodes: formattedFilter.userFilters?.producerIdsAndSourceCodes?.map(
        ({userId, sourceCodes}) => {
          return {
            userId,
            sourceCodes: withAmsProducerCodes ? sourceCodes : undefined,
          };
        }
      ),
      accountManagerIdsAndSourceCodes: formattedFilter.userFilters?.accountManagerIdsAndSourceCodes?.map(
        ({userId, sourceCodes}) => {
          return {
            userId,
            sourceCodes: withAmsProducerCodes ? sourceCodes : undefined,
          };
        }
      ),
    },
    period: formattedPeriod,
    additionalPeriods:
      formattedPeriod.type !== PeriodType.InForce && formattedPeriod.inclusionDateType !== InclusionDateType.Transaction
        ? formattedFilter.additionalPeriods?.map(formatPolicyFilterPeriod)
        : undefined,
  };
};

export const formatPolicyFilterPeriod = <T extends Period>(period: Falsy<T>) => {
  if (!period || isEmpty(period)) {
    return initialFilter.period as Period;
  }

  if (period.type === PeriodType.InForce) {
    return {
      ...period,
      from: undefined,
      to: undefined,
      inclusionDateType: undefined,
      timestamp: period.timestamp,
    };
  }

  if ([PeriodType.Ytd, PeriodType.Rolling_12Months].includes(period.type as PeriodType)) {
    return {
      ...period,
      from: undefined,
      to: undefined,
      timestamp: formatDateToString(),
    };
  }

  if (period.type === PeriodType.FromTo) {
    return {
      ...period,
      timestamp: undefined,
      from: formatDateOrNullWithFallback(
        period.from,
        formatDateToString(dayjs(formatDateToString()).subtract(1, 'year'))
      ),
      to: formatDateOrNullWithFallback(period.to, formatDateToString()),
    };
  }

  return period;
};

export const hasInvalidDates = (period?: Period | null, additionalPeriods?: Period[] | null) => {
  const {from, to, timestamp, type, inclusionDateType} = period || {};
  const additionalPeriod = additionalPeriods?.[0];

  if (type === PeriodType.InForce) {
    return timestamp === undefined || (!dayjs(timestamp).isValid() && timestamp !== null);
  }

  let hasPeriodErrors = false;
  let hasAdditionalPeriodErrors = false;

  if (type === PeriodType.FromTo) {
    hasPeriodErrors = hasInvalidDateRange(from, to, inclusionDateType !== InclusionDateType.Expiration);
  }

  if (additionalPeriod) {
    hasAdditionalPeriodErrors = hasInvalidDateRange(additionalPeriod.from, additionalPeriod.to);
  }

  return hasPeriodErrors || hasAdditionalPeriodErrors;
};
